import { ArrowDropDown, ArrowDropUp, Close, FilterList, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  TextField,
  InputAdornment,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { IDateRange, IFieldWalkthroughTerritory, INeighborhood, ILot } from '../../../models';
import useCollapse from 'react-collapsed';
import {
  getFieldWalkthroughCreatedBy,
  getFieldWalkthroughTerritories
} from '../../../fetch';
import { DateRangePicker, SearchSection, SMBSelect } from '../../../components';
import { getCommonNeighborhoodLots, getProductionNeighborhoods } from '../../../fetch/lookups';

interface IFieldWalkthroughFilterProps {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean, clearSearch?: boolean) => void;
  hasAppliedFilters?: boolean;
  setHasAppliedFilters: (val: boolean) => void;
  setSearchValue: (val: string) => void;
  searchValue: string;
  selectedTerritory: IFieldWalkthroughTerritory | null;
  setSelectedTerritory: (val: IFieldWalkthroughTerritory | null) => void;
  selectedNeighborhood: string | null;
  setSelectedNeighborhood: (val: string | null) => void;
  selectedLotNumber: string | null;
  setSelectedLotNumber: (val: string | null) => void;
  selectedDateRange: IDateRange | null;
  setSelectedDateRange: Dispatch<SetStateAction<IDateRange>>;
  selectedCreatedBy: string | null;
  setSelectedCreatedBy: (val: string | null) => void;
  toggleCompleted: (val: boolean) => void;
  isComplete: boolean;
}

export const FieldWalkthroughFilters: FC<IFieldWalkthroughFilterProps> = ({
  isLoading,
  applyFilters,
  hasAppliedFilters,
  setHasAppliedFilters,
  setSearchValue,
  searchValue,
  selectedTerritory,
  setSelectedTerritory,
  selectedNeighborhood,
  setSelectedNeighborhood,
  selectedLotNumber,
  setSelectedLotNumber,
  selectedDateRange,
  setSelectedDateRange,
  selectedCreatedBy,
  setSelectedCreatedBy,
  isComplete,
  toggleCompleted
}) => {
  const classes = useStyles();
  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse({
    defaultExpanded: true
  });

  const [searchedValue, setSearchedValue] = useState<string>('');

  const [isLoadingTerritories, setTerritoriesLoading] = useState(false);
  const [territories, setTerritories] = useState<IFieldWalkthroughTerritory[]>([]);
  const fetchTerritories = async () => {
    setTerritoriesLoading(true);
    try {
      const res = await getFieldWalkthroughTerritories();
      setTerritories(res);
    } catch (error) {
      console.log(error);
    } finally {
      setTerritoriesLoading(false);
    }
  };

  const [isLoadingNeighborhoods, setIsLoadingNeighborhoods] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState<INeighborhood[]>([]);
  const fetchNeighborhoods = async () => {
    setIsLoadingNeighborhoods(true);
    try {
      const res = await getProductionNeighborhoods({ territoryId: selectedTerritory?.territoryId });
      setNeighborhoods(res.records);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingNeighborhoods(false);
    }
  };

  const [isLoadingLots, setIsLoadingLots] = useState(false);
  const [lots, setLots] = useState<ILot[]>([]);
  const fetchNeighborhoodLots = async () => {
    setIsLoadingLots(true);
    try {
      const res = await getCommonNeighborhoodLots({ neighborhoodId: Number(selectedNeighborhood) });
      setLots(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingLots(false);
    }
  };

  const [isLoadingCreatedBy, setIsloadingCreatedBy] = useState(false);
  const [createdBy, setCreatedBy] = useState<string[]>([]);
  const fetchWalkthroughCreatedBy = async () => {
    setIsloadingCreatedBy(true);
    try {
      const res = await getFieldWalkthroughCreatedBy();
      setCreatedBy(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsloadingCreatedBy(false);
    }
  };

  const handleDisableButton = () => {
    if (
      isLoading ||
      isLoadingTerritories ||
      isLoadingNeighborhoods ||
      isLoadingLots ||
      isLoadingCreatedBy
    )
      return true;
    return false;
  };

  // Used to help determine when to toggle hasAppliedFilters on/off for search specific functions
  const hasSelectedFilters = () => {
    return (
      !!selectedTerritory ||
      !!selectedNeighborhood ||
      !!selectedLotNumber ||
      !!selectedDateRange?.startDate ||
      !!selectedDateRange?.endDate ||
      !!selectedCreatedBy ||
      isComplete
    );
  };

  useEffect(() => {
    fetchTerritories();
    if (!selectedCreatedBy) {
      fetchWalkthroughCreatedBy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Fetch new neighborhoods when territory changes
    if (selectedTerritory?.territoryId) {
      fetchNeighborhoods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerritory]);

  useEffect(() => {
    // Fetch new lots when neighborhood changes
    if (selectedNeighborhood) {
      fetchNeighborhoodLots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNeighborhood]);

  return (
    <>
      <Box
        display="flex"
        width={{
          xs: '100%',
          sm: 'auto'
        }}
      >
        <Button
          color="secondary"
          variant="contained"
          className={classes.mobileButton}
          startIcon={<FilterList />}
          endIcon={isExpanded ? <ArrowDropUp /> : <ArrowDropDown />}
          disabled={isLoading}
          {...getToggleProps()}
        >
          Filters
        </Button>
      </Box>
      <Box {...getCollapseProps()}>
        <Grid container alignItems="center" spacing={1} marginTop={theme => theme.spacing(0.5)}>
          <Grid item xs={12} sm={6} lg={4} xl={3}>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder="Search by plan description..."
              name="search"
              value={searchValue}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    className={classes.searchIcon}
                    onClick={() => {
                      if (searchValue.length > 0) {
                        setHasAppliedFilters(true);
                      } else if (!hasSelectedFilters()) {
                        setHasAppliedFilters(false);
                      }
                      setSearchedValue(searchValue);
                      applyFilters();
                    }}
                  >
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment
                    position="end"
                    className={classes.searchIcon}
                    onClick={() => {
                      setSearchValue('');
                    }}
                  >
                    <Close />
                  </InputAdornment>
                ) : null
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  if (searchValue.length > 0) {
                    setHasAppliedFilters(true);
                  } else if (!hasSelectedFilters()) {
                    setHasAppliedFilters(false);
                  }
                  setSearchedValue(searchValue);
                  applyFilters();
                }
              }}
              onChange={e => {
                setSearchValue(e.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4} xl={3}>
            <SMBSelect
              variant="outlined"
              label={isLoadingTerritories ? 'Loading...' : 'Territory'}
              value={selectedTerritory ? selectedTerritory.territoryId : ''}
              disabled={isLoadingTerritories || isLoading}
              onChange={e => {
                const selectedTerritory = territories.find(
                  territory => Number(e.target.value) === territory.territoryId
                );
                if (selectedTerritory) {
                  setSelectedTerritory(selectedTerritory);
                  setSelectedNeighborhood(undefined);
                  setSelectedLotNumber(undefined);
                }
              }}
              handleClear={() => {
                setSelectedTerritory(null);
                setSelectedNeighborhood(undefined);
                setSelectedLotNumber(undefined);
              }}
            >
              {territories &&
                !isLoadingTerritories &&
                territories.map((territory, index) => {
                  return (
                    <MenuItem
                      key={`${territory.territoryId}-${index}`}
                      value={territory.territoryId}
                    >
                      {territory.territoryName}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6} lg={4} xl={3}>
            <SMBSelect
              variant="outlined"
              label={isLoadingTerritories ? 'Loading...' : 'Neighborhood'}
              disabled={
                isLoadingTerritories || isLoading || isLoadingNeighborhoods || !selectedTerritory
              }
              id="neighborhood"
              value={selectedNeighborhood ? selectedNeighborhood : ''}
              onChange={e => {
                setSelectedNeighborhood(e.target.value);
                if (e.target.value) {
                  setSelectedLotNumber(undefined);
                }
              }}
              handleClear={() => {
                setSelectedNeighborhood(undefined);
                setSelectedLotNumber(undefined);
              }}
            >
              {!isLoadingNeighborhoods && neighborhoods && neighborhoods.length === 0 && (
                <MenuItem disabled>No Neighborhoods available for this territory</MenuItem>
              )}
              {neighborhoods &&
                !isLoadingNeighborhoods &&
                neighborhoods.map((neighborhood, index) => {
                  return (
                    <MenuItem
                      key={`${neighborhood.neighborhoodId}-${index}`}
                      value={neighborhood.neighborhoodId}
                    >
                      {neighborhood.name} Phase {neighborhood.phase}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6} lg={4} xl={3}>
            <SMBSelect
              variant="outlined"
              label={isLoadingLots ? 'Loading...' : 'Lot'}
              disabled={
                isLoadingTerritories ||
                isLoading ||
                isLoadingNeighborhoods ||
                isLoadingLots ||
                !selectedTerritory ||
                !selectedNeighborhood
              }
              value={selectedLotNumber ? selectedLotNumber : ''}
              onChange={e => {
                setSelectedLotNumber(e.target.value);
              }}
              handleClear={() => {
                setSelectedLotNumber(undefined);
              }}
            >
              {!isLoadingNeighborhoods && neighborhoods && neighborhoods.length === 0 && (
                <MenuItem disabled>No Neighborhoods available for this territory</MenuItem>
              )}
              {lots &&
                !isLoadingLots &&
                lots.map((lot, index) => {
                  return (
                    <MenuItem key={`${index}`} value={lot.lotNumber}>
                      {lot.description}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6} lg={4} xl={3}>
            <DateRangePicker
              id="createdOnDateRange"
              label="Created On"
              value={selectedDateRange}
              isDisabled={isLoading}
              onChange={value => {
                if (value) {
                  setSelectedDateRange(value);
                }
              }}
              fullWidth
              inputSize="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4} xl={3}>
            <SMBSelect
              variant="outlined"
              label={isLoadingCreatedBy ? 'Loading...' : 'Created By'}
              disabled={isLoading || isLoadingCreatedBy}
              value={selectedCreatedBy ? selectedCreatedBy : ''}
              onChange={e => {
                setSelectedCreatedBy(e.target.value);
              }}
              handleClear={() => {
                setSelectedCreatedBy(null);
              }}
            >
              {createdBy &&
                !isLoadingCreatedBy &&
                createdBy.map((user, index) => {
                  return (
                    <MenuItem key={`${user}-${index}`} value={user}>
                      {user}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Grid item xs={12} sm={6} lg={4} xl={3}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={isComplete}
                  id="active-inactive"
                  disabled={isLoading}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    toggleCompleted(e.target.checked);
                  }}
                />
              }
              label="Completed"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              color="primary"
              variant="contained"
              className={classes.button}
              disabled={handleDisableButton()}
              startIcon={<FilterList />}
              onClick={() => {
                if (searchValue.length > 0 || hasSelectedFilters()) {
                  setHasAppliedFilters(true);
                } else {
                  setHasAppliedFilters(false);
                }
                if (searchValue.length > 0) {
                  setSearchedValue(searchValue);
                }
                applyFilters();
              }}
            >
              Apply Filters
            </Button>
            {hasAppliedFilters && (
              <Button
                color="inherit"
                className={clsx(classes.button, classes.resetButton)}
                variant="contained"
                disabled={isLoading}
                startIcon={<Close />}
                onClick={() => {
                  setHasAppliedFilters(false);
                  setSearchValue('');
                  setSearchedValue('');
                  setSelectedTerritory(null);
                  setSelectedNeighborhood(null);
                  setSelectedLotNumber(null);
                  setSelectedDateRange(undefined);
                  setSelectedCreatedBy(null);
                  toggleCompleted(false);
                  applyFilters(true, true);
                }}
              >
                Reset
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
      {searchedValue && (
        <Box marginTop={theme => theme.spacing(1)}>
          <SearchSection
            searchedValue={searchedValue}
            handleClearSearch={() => {
              setSearchValue('');
              setSearchedValue('');
              if (!hasSelectedFilters()) {
                setHasAppliedFilters(false);
              }
              applyFilters(undefined, true);
            }}
          />
        </Box>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  button: {
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  }
}));
